import { environment } from "../environment";
import { AuthConnector } from "../serverConnectors/authConnector";
import { Roles } from "../types";
import { AuthLocationHintService } from "./auth-location-hint-service";

const FLOW_KEY = "linkedinAuthFlow";
type LinkedInAction = "LOGIN" | "REGISTER";
interface PendingFlow { state: string; verifier: string; }

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export class LinkedInAuthClient {
  constructor(
    private readonly connector: AuthConnector,
    private readonly locationHints = new AuthLocationHintService(),
    private readonly navigate: (url: string) => void = url => window.location.assign(url),
  ) {}

  async start(action: LinkedInAction, role?: Roles): Promise<void> {
    if (!environment.linkedInAuthEnabled) {
      throw new Error("LinkedIn sign-in is not available yet. Please use another method.");
    }
    const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
    const response = await this.connector.axios.post<{ authorizationUrl: string; state: string }>(
      "/sso/linkedin/authorize", { action, role, verifierChallenge: base64url(new Uint8Array(digest)) },
    );
    const { authorizationUrl, state } = response.data;
    const url = new URL(authorizationUrl);
    if (url.origin !== "https://www.linkedin.com" || url.pathname !== "/oauth/v2/authorization" ||
        !/^[A-Za-z0-9_-]{43}$/.test(state) || url.searchParams.get("state") !== state) {
      throw new Error("LinkedIn sign-in could not be started. Please try again.");
    }
    sessionStorage.setItem(FLOW_KEY, JSON.stringify({ state, verifier }));
    this.navigate(authorizationUrl);
  }

  async complete(search: string): Promise<string> {
    const params = new URLSearchParams(search);
    const pending = sessionStorage.getItem(FLOW_KEY);
    sessionStorage.removeItem(FLOW_KEY);
    let flow: PendingFlow | null = null;
    try { flow = pending ? JSON.parse(pending) : null; } catch { /* Invalid/expired browser state. */ }
    if (!flow || !/^[A-Za-z0-9_-]{43}$/.test(flow.verifier) || params.get("state") !== flow.state) {
      throw new Error("LinkedIn sign-in expired or is invalid. Please start again.");
    }
    if (params.has("error")) {
      throw new Error("LinkedIn sign-in was cancelled or denied. Please try again.");
    }
    const code = params.get("code");
    if (!code) throw new Error("LinkedIn did not return an authorization code. Please try again.");
    const response = await this.connector.axios.post<{ customToken: string }>(
      "/sso/linkedin/complete", { code, state: flow.state, verifier: flow.verifier },
      { headers: await this.locationHints.headers() },
    );
    if (!response.data.customToken) throw new Error("LinkedIn sign-in could not be completed.");
    return response.data.customToken;
  }
}
